import React, {useEffect, useRef, useState} from "react";
import * as fabric from 'fabric';
import {toast} from "react-toastify";
import {useDispatch} from "react-redux";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";

import {BrushIcon} from "../icons/BrushIcon";
import {AddTextIcon} from "../icons/AddTextIcon";
import {AddImageIcon} from "../icons/AddImageIcon";
import {AddText} from "../Modals/AddText/AddText";
import {ActionFn, UiModal} from "../Modals/UiModals";
import {addModal, removeModal} from "../../redux/actions/Modals";
import { RemoveIcon } from "../icons/RemoveIcon";

import "./WhiteBoard.scss";

interface IProps {
    connection: any;
    isAdmin: boolean;
    resize?: boolean;
    history: any[];
    addHistoryItem: (item: any) => void;
}

export const WhiteBoard = (props: IProps) => {
    const {connection, isAdmin, resize, history, addHistoryItem} = props;
    const [canvas, setCanvas] = useState<any>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isTextMode, setIsTextMode] = useState(false);
    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [textCounter, setTextCounter] = useState(0);
    const [imgCounter, setImgCounter] = useState(0);
    const [maxFileSize, setMaxFileSize] = useState(1);
    const canvasEl = useRef(null);
    const canvasElParent = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();

    useEffect(() => {
        if (connection !== null && canvas !== null) {
            connection?.on("DrawPathResponse", (message: any) => {
                const historyItem = {eventName: "DrawPathResponse", request: message};
                addHistoryItem(historyItem);
                addCanvasItem(canvas, historyItem);
            });
            connection?.on("DrawTextResponse", (message: any) => {
                const historyItem = {eventName: "DrawTextResponse", request: message};
                addHistoryItem(historyItem);
                addCanvasItem(canvas, historyItem);
            });
            connection?.on("LoadImageResponse", (message: any) => {
                const historyItem = {eventName: "LoadImageResponse", request: message};
                addHistoryItem({eventName: "LoadImageResponse", request: message});
                addCanvasItem(canvas, historyItem);
            });
            connection?.on("DragObjectResponse", (message: any) => {
                const historyItem = {eventName: "DragObjectResponse", request: message};
                addHistoryItem({eventName: "DragObjectResponse", request: message});
                addCanvasItem(canvas, historyItem);
            });
            connection?.on("ResizeObjectResponse", (message: any) => {
                const historyItem = {eventName: "ResizeObjectResponse", request: message};
                addHistoryItem({eventName: "ResizeObjectResponse", request: message});
                addCanvasItem(canvas, historyItem);
            });
            connection?.on("RemoveObjectResponse", (message: any) => {
                const historyItem = {eventName: "RemoveObjectResponse", request: message};
                addHistoryItem({eventName: "RemoveObjectResponse", request: message});
                addCanvasItem(canvas, historyItem);
            });
            connection?.on("ScaleObjectResponse", (message: any) => {
                const historyItem = {eventName: "ScaleObjectResponse", request: message};
                addHistoryItem({eventName: "ScaleObjectResponse", request: message});
                addCanvasItem(canvas, historyItem);
            });
            connection?.on("ChangeTextResponse", (message: any) => {
                const historyItem = {eventName: "ChangeTextResponse", request: message};
                addHistoryItem({eventName: "ChangeTextResponse", request: message});
                addCanvasItem(canvas, historyItem);
            });

            connection?.on("ZoomResponse", (message: any) => {
                const historyItem = {eventName: "ZoomResponse", request: message};
                addHistoryItem({eventName: "ZoomResponse", request: message});
                addCanvasItem(canvas, historyItem);
            });

            connection?.on("MaxFileSizeResponse", (message: any) => {
                setMaxFileSize(message?.maxFileSize);
            });

            if (isAdmin) {
                let linesCount = 0;
                canvas.on('path:created', (event: any) => {
                    const name = `line-${linesCount}`;
                    const request = {path: event.path.path, name};
                    addHistoryItem({eventName: "DrawPathResponse", request});
                    sendMessageSignal("DrawPath", request);
                    event.path.selectable = false;
                    event.path.name = name;
                    linesCount++;
                });
                canvas.on('mouse:dblclick', (event: any) => {
                    if (event.target?.name?.includes("text")) {
                        canvas.isDrawingMode = false;
                        setIsDrawing(false);
                        changeDeleteMode(false);
                        setIsTextMode(true);
                        const component = AddText({
                            text: event.target.text,
                            onSuccess: (text: any) => {
                                changeText(canvas, event.target.name, text);
                                const request = {text, name: event.target.name};
                                addHistoryItem({eventName: "ChangeTextResponse", request});
                                sendMessageSignal("ChangeText", request);
                                close();
                            }
                        });
                        const close: ActionFn = () => {setIsTextMode(false); dispatch(removeModal(modal))};
                        const modal: UiModal = {component, close};
                        dispatch(addModal(modal));
                    }
                });
                canvas.on('object:modified', (event: any) => {
                    if (event.action === "drag") {
                        const request = {
                            name: event.target.name,
                            left: event.target.left.toString(),
                            top: event.target.top.toString()
                        };
                        addHistoryItem({eventName: "DragObjectResponse", request});
                        sendMessageSignal("DragObject", request);
                    }
                    if (["scale", "scaleX", "scaleY"].includes(event.action)) {
                        const request = {
                            name: event.target.name,
                            left: event.target.left.toString(),
                            top: event.target.top.toString(),
                            scaleX: event.target.scaleX.toString(),
                            scaleY: event.target.scaleY.toString()
                        };
                        addHistoryItem({eventName: "ScaleObjectResponse", request});
                        sendMessageSignal("ScaleObject", request);
                    }
                    if (event.action === "resizing") {
                        const request = {
                            name: event.target.name,
                            width: event.target.width.toString()
                        };
                        addHistoryItem({eventName: "ResizeObjectResponse", request});
                        sendMessageSignal("ResizeObject", request);
                    }
                });
                canvas.on('mouse:wheel', (opt: any) => {
                    var delta = opt.e.deltaY;
                    var zoom = canvas.getZoom();
                    zoom *= 0.999 ** delta;
                    if (zoom > 20) zoom = 20;
                    if (zoom < 0.01) zoom = 0.01;
                    zoomToPoint(canvas, zoom, opt.e.offsetX, opt.e.offsetY);
                    const request = {
                        value: zoom.toString(),
                        offsetX: opt.e.offsetX.toString(),
                        offsetY: opt.e.offsetY.toString()
                    };
                    addHistoryItem({eventName: "ZoomResponse", request});
                    sendMessageSignal("Zoom", request);

                    opt.e.preventDefault();
                    opt.e.stopPropagation();
                });
            }
        }
    }, [connection, canvas]);

    const setCurrentDimensions = (dimensionCanvas: any, resize = false) => {
        if (dimensionCanvas && canvasElParent) {
            let width = canvasElParent?.current?.clientWidth || 0;
            let height = canvasElParent?.current?.clientHeight || 0;
            if (canvasElParent?.current?.clientWidth && resize) {
                width = canvasElParent?.current?.clientWidth - (canvasElParent?.current?.clientWidth * 0.3);
            }
            if (height > 0) {
                dimensionCanvas.setHeight(height);
            }
            if (width > 0) {
                dimensionCanvas.setWidth(width);
            }
            dimensionCanvas.renderAll();
        }
    }

    useEffect(() => {
        const fabricCanvas = new fabric.Canvas(canvasEl.current as any);
        const resizeCanvas = () => {
            setCurrentDimensions(fabricCanvas);
        };
        setCurrentDimensions(fabricCanvas);

        window.addEventListener('resize', resizeCanvas, false);
        const brush = new fabric.PencilBrush(fabricCanvas);
        brush.color = 'red';
        brush.width = 5;
        fabricCanvas.freeDrawingBrush = brush;
        setCanvas(fabricCanvas);
        
        if (history?.length > 0) {
            updateCanvasHistory(fabricCanvas, history);
        }

        return () => {
            fabricCanvas.dispose()
            window.removeEventListener('resize', resizeCanvas)
        }
    }, []);
    
    const updateCanvasHistory = async (fabricCanvas: any, history: any) => {
        for (let item of history) {
            await addCanvasItem(fabricCanvas, item);
        }
    }
    
    useEffect(() => {
        if (canvas) {
            setCurrentDimensions(canvas, resize);
        }
    }, [canvas, resize]);
    
    const addCanvasItem = async (fabricCanvas: any, historyItem: any) => {
        const message = historyItem.request;
        const eventName = historyItem.eventName;
        
        if (eventName === "DrawPathResponse") {
            fabricCanvas?.add(new fabric.Path(message?.path, {
                stroke: 'red',
                strokeWidth: 5,
                strokeLineCap: "round",
                strokeLineJoin: "round",
                strokeMiterLimit: 10,
                fill: undefined,
                selectable: false,
                name: message.name
            }));
        } else if (eventName === "DrawTextResponse") {
            setTextCounter(value => value + 1);
            addText(fabricCanvas, message.text, message.name);
        } else if (eventName === "LoadImageResponse") {
            setImgCounter(value => value + 1);
            await addImage(fabricCanvas, message.image, message.name);
        } else if (eventName === "DragObjectResponse") {
            const objects = fabricCanvas.getObjects();
            const object = objects.find((x: any) => x.name === message.name);
            if (object) {
                object.top = +message.top;
                object.left = +message.left;
                object.dirty = true;
                object.setCoords();
                fabricCanvas.renderAll();
            }
        } else if (eventName === "ResizeObjectResponse") {
            const objects = fabricCanvas.getObjects();
            const object = objects.find((x: any) => x.name === message.name);
            if (object) {
                object.width = +message.width;
                object.dirty = true;
                object.setCoords();
                fabricCanvas.renderAll();
            }
        } else if (eventName === "RemoveObjectResponse") {
            const objects = fabricCanvas.getObjects();
            const object = objects.find((x: any) => x.name === message.name);
            if (object) {
                removeObject(fabricCanvas, object);
            }
        } else if (eventName === "ScaleObjectResponse") {
            const objects = fabricCanvas.getObjects();
            const object = objects.find((x: any) => x.name === message.name);
            if (object) {
                object.top = +message.top;
                object.left = +message.left;
                object.scaleX = +message.scaleX;
                object.scaleY = +message.scaleY;
                object.dirty = true;
                object.setCoords();
                fabricCanvas.renderAll();
            }
        } else if (eventName === "ChangeTextResponse") {
            changeText(fabricCanvas, message.name, message.text);
        } else if (eventName === "ZoomResponse") {
            zoomToPoint(fabricCanvas, parseFloat(message.value), +message.offsetX, +message.offsetY);
        }
    }
    

    const changeDrawingMode = () => {
        canvas.isDrawingMode = !isDrawing;
        setIsDrawing(!isDrawing);
        setIsTextMode(false);
        changeDeleteMode(false);
    }
    
    const changeDeleteMode = (mode: boolean) => {
        if (mode) {
            canvas.isDrawingMode = false
            setIsDrawing(false);
            setIsTextMode(false);
            canvas.getObjects().forEach((obj: any) => {
                obj.on('mousedown', (opt: any) => {
                    const request = {
                        name: opt.target.name
                    };
                    addHistoryItem({eventName: "RemoveObjectResponse", request});
                    sendMessageSignal("RemoveObject", request);
                    removeObject(canvas, opt.target);
                });
            })
        } else {
            canvas.getObjects().forEach((obj: any) => {
                obj.off('mousedown');
            })
        }
        setIsDeleteMode(mode);
    }
    
    const removeObject = (fabricCanvas: any, obj: any) => {
        fabricCanvas.remove(obj);
    }

    const addText = (fabricCanvas: any, text: any, name: any) => {
        fabricCanvas.add(new fabric.Textbox(text, {
            fontSize: 26,
            fontFamily: 'Roboto',
            stroke: 'red',
            fill: 'red',
            top: 150,
            left: 150,
            lockScalingX: true,
            lockScalingY: true,
            lockRotation: true,
            editable: false,
            width: text.length * 15,
            name
        }));
    }

    const addImage = async (fabricCanvas: any, image: any, name: any) => {
        const img: any = await fabric.Image.fromURL(image);
        img.scaleToHeight(250);
        img.scaleToWidth(250);
        img.left = 100;
        img.top = 100;
        img.lockRotation = true;
        img.name = name;
        fabricCanvas.add(img);
    }

    const zoomToPoint = (fabricCanvas: any, zoomValue: any, offsetX: any, offsetY: any) => {
        fabricCanvas.zoomToPoint({x: offsetX, y: offsetY}, zoomValue);
    }

    const handleFileChange = (event: any) => {
        canvas.isDrawingMode = false;
        setIsDrawing(false);
        setIsDeleteMode(false);
        const file = event.target.files[0];
        if (!file) {
            return;
        }
        const filesize = +((file?.size / 1024) / 1024).toFixed(4);
        if (filesize >= maxFileSize) {
            toast.error(`Максимальный размер загружаемого файла ${maxFileSize} мегабайт`);
            return;
        }
        if (!["image/png", "application/pdf", "image/jpeg"].includes(file.type)) {
            return;
        }
        const reader = new FileReader();
        if (file.type !== "application/pdf") {
            reader.addEventListener("load", readImageFile);
            reader.readAsDataURL(event.target.files?.[0]);
        } else {
            reader.addEventListener("load", readPdfFile);
            reader.readAsArrayBuffer(file);
        }
    }

    const readImageFile = (event: any) => {
        const name = `image-${imgCounter}`;
        const request = {image: event.target.result, name};
        addHistoryItem({eventName: "LoadImageResponse", request});
        sendMessageSignal("LoadImage", request);
        setImgCounter(value => value + 1);
        addImage(canvas, event.target.result, name);
    }

    const readPdfFile = (event: any) => {
        var typedarray = new Uint8Array(event.target.result);
        pdfjsLib.GlobalWorkerOptions.workerSrc = "pdfjs-dist/build/pdf.worker.entry";
        pdfjsLib?.getDocument(typedarray)?.promise?.then(pdf => {
            pdf.getPage(1).then(page => {
                var viewport = page.getViewport({scale: 1});
                var canvasEl = document.createElement("canvas")
                canvasEl.height = viewport.height;
                canvasEl.width = viewport.width;
                const context: any = {
                    canvasContext: canvasEl.getContext('2d'),
                    viewport: viewport
                }
                page.render(context).promise.then(() => {
                    var bg = canvasEl.toDataURL("image/png");
                    const name = `image-${imgCounter}`;
                    const request = {image: bg, name};
                    addHistoryItem({eventName: "LoadImageResponse", request});
                    sendMessageSignal("LoadImage", request);
                    addImage(canvas, bg, name);
                    setImgCounter(value => value + 1);
                });
            });
        });
    }

    const onAddText = () => {
        canvas.isDrawingMode = false;
        setIsDrawing(false);
        changeDeleteMode(false);
        setIsTextMode(true);
        const component = AddText({
            text: "",
            onSuccess: (text: any) => {
                const name = `text-${textCounter}`;
                addText(canvas, text, name);
                const request = {text, name};
                addHistoryItem({eventName: "DrawTextResponse", request});
                sendMessageSignal("DrawText", request);
                setTextCounter(textCounter + 1);
                close();
            }
        });
        const close: ActionFn = () => {setIsTextMode(false); dispatch(removeModal(modal))};
        const modal: UiModal = {component, close};
        dispatch(addModal(modal));
    }

    const changeText = (fabricCanvas: any, name: string, text: string) => {
        const objects = fabricCanvas.getObjects();
        const object = objects.find((x: any) => x.name === name);
        if (object) {
            object.text = text;
            object.dirty = true;
            object.setCoords();
            fabricCanvas.renderAll();
        }
    }

    const sendMessageSignal = (methodName: any, request: any) => {
        if (connection !== null) {
            if (request !== null) {
                connection.invoke(methodName, request);
            } else {
                connection.invoke(methodName);
            }
        }
    }

    return <div ref={canvasElParent} style={{width: '100%', height: 'calc(100% - 250px)', marginTop: 100, position: 'relative', background: '#242424'}}>
        <canvas width="720" height="480" ref={canvasEl}/>
        {isAdmin && <div className="board-buttons-container">
            <div className={`main-chat-btns-container`}>
                <div className={`chat-btn ${!isDrawing ? 'chat-btn_off' : ''}`}
                     onClick={changeDrawingMode}><BrushIcon/></div>
                <div className={`chat-btn ${!isTextMode ? 'chat-btn_off' : ''}`}
                     onClick={onAddText}><AddTextIcon/></div>
                <div className={`chat-btn ${!isDeleteMode ? 'chat-btn_off' : ''}`}
                     onClick={() => changeDeleteMode(!isDeleteMode)}><RemoveIcon /></div>
                <label className={`chat-btn chat-btn_off`}>
                    <input style={{display: 'none'}} type="file" accept="image/png, application/pdf, image/jpeg"
                           onChange={handleFileChange}/>
                    <AddImageIcon/>
                </label>
            </div>
        </div>}
    </div>;
}